import '../fonts.js';
import { useState, type ReactNode } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { I18nextProvider } from 'react-i18next';
import { PanelApi } from '@iptv/api-client';
import { ConfirmProvider, ToastProvider } from '../components/feedback.js';
import { PanelAuthProvider, type PanelAudience } from './auth.js';
import { createPanelI18n } from './i18n.js';
import { createQueryClient } from './query.js';
import { initTheme } from './theme.js';

/**
 * Wires every provider a panel needs. Panels are served on the same origin as the API (`/v1` is
 * reverse-proxied), so the HttpOnly refresh cookie and the CSRF cookie are first-party.
 */
export function PanelRoot({
  audience,
  apiBaseUrl,
  children,
}: {
  audience: PanelAudience;
  apiBaseUrl?: string;
  children: ReactNode;
}) {
  const [deps] = useState(() => {
    initTheme();
    return {
      api: new PanelApi(apiBaseUrl ?? window.location.origin),
      i18n: createPanelI18n(),
      queryClient: createQueryClient(),
    };
  });
  return (
    <I18nextProvider i18n={deps.i18n}>
      <QueryClientProvider client={deps.queryClient}>
        <ToastProvider>
          <ConfirmProvider>
            <PanelAuthProvider api={deps.api} audience={audience}>
              {children}
            </PanelAuthProvider>
          </ConfirmProvider>
        </ToastProvider>
      </QueryClientProvider>
    </I18nextProvider>
  );
}
